import React, { useCallback, useMemo, useState } from 'react'

import { faEnvelope } from '@fortawesome/pro-regular-svg-icons'
import { Button } from 'antd'
import { Input, Modal, Select, Text, VStack } from 'govwell-ui'
import { SelectOption } from 'govwell-ui/components/Select/types'
import { AicaEmailDigestFrequency } from 'types/graphql'

import { DIGEST_FREQUENCY_OPTIONS } from './digestConstants'

type Props = {
  onClose: () => void
  onSend: (emailAddress: string, frequency: AicaEmailDigestFrequency) => Promise<void>
  isLoading: boolean
}

const getOptionKey = (option: SelectOption<AicaEmailDigestFrequency>) => option.value

const SendTestDigestModal = ({ onClose, onSend, isLoading }: Props) => {
  const [emailAddress, setEmailAddress] = useState('')
  const [frequency, setFrequency] = useState<AicaEmailDigestFrequency>('Weekly')

  const handleSubmit = useCallback(async () => {
    if (!emailAddress.trim()) {
      return
    }
    await onSend(emailAddress.trim(), frequency)
  }, [emailAddress, frequency, onSend])

  const selectedFrequencyOption = useMemo(
    () => DIGEST_FREQUENCY_OPTIONS.find((o) => o.value === frequency),
    [frequency]
  )

  const handleFrequencyChange = useCallback(
    (option: SelectOption<AicaEmailDigestFrequency> | undefined) => {
      if (option) {
        setFrequency(option.value)
      }
    },
    []
  )

  const isSubmitDisabled = !emailAddress.trim() || isLoading

  return (
    <Modal onClose={onClose}>
      <Modal.Header icon={faEnvelope}>Send Test Digest</Modal.Header>
      <Modal.Content>
        <VStack gap={4}>
          <Text.Body>
            Send a test digest email to preview what subscribers will receive. The digest will use
            real data from the previous period.
          </Text.Body>
          <Input
            label="Email Address"
            type="email"
            placeholder="Enter email address"
            value={emailAddress}
            onValueChange={setEmailAddress}
          />
          <Select
            label="Digest Type"
            options={DIGEST_FREQUENCY_OPTIONS}
            selectedOption={selectedFrequencyOption}
            onSelectedOptionChange={handleFrequencyChange}
            getOptionKey={getOptionKey}
          />
        </VStack>
      </Modal.Content>
      <Modal.Footer>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          type="primary"
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
          loading={isLoading}
        >
          Send test email
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default React.memo(SendTestDigestModal)
